import { useRef, useState } from "react";
import { ghostBtnClass } from "@/components/service/serviceShared";
import {
  SERVICE_REQUEST_PHOTO_ACCEPT,
  uploadServiceRequestAttachment,
  type ServiceRequestAttachment,
} from "@/lib/service/requestAttachmentsClient";

export function ServiceRequestPhotoUpload(props: {
  requestId: string;
  required: boolean;
  attachments: ServiceRequestAttachment[];
  onAttachmentsChange: (next: ServiceRequestAttachment[]) => void;
  disabled?: boolean;
}) {
  const { requestId, required, attachments, onAttachmentsChange, disabled = false } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFiles(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true);
    setError(null);
    const next = [...attachments];
    for (const file of Array.from(files)) {
      const res = await uploadServiceRequestAttachment(requestId, file);
      if (!res.ok) {
        setError(res.error);
        break;
      }
      next.push(res.attachment);
    }
    onAttachmentsChange(next);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={SERVICE_REQUEST_PHOTO_ACCEPT}
          capture="environment"
          multiple
          className="hidden"
          disabled={disabled || busy}
          onChange={(e) => void handleFiles(e.target.files)}
        />
        <button
          type="button"
          className={ghostBtnClass}
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? "Uploading…" : "Add site photo"}
        </button>
        {required ? (
          <span className="text-xs text-amber-800 dark:text-amber-200">
            Required before submit ({attachments.length} attached)
          </span>
        ) : (
          <span className="text-xs text-gray-500 dark:text-zinc-400">
            Optional ({attachments.length} attached)
          </span>
        )}
      </div>
      {error ? <p className="text-xs text-red-700 dark:text-red-300">{error}</p> : null}
      {attachments.length > 0 ? (
        <ul className="text-xs text-gray-600 dark:text-zinc-400 space-y-0.5">
          {attachments.map((a) => (
            <li key={a.id}>{a.filename || a.id.slice(0, 8)}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function canSubmitServiceRequestWithPhotos(
  photosRequired: boolean,
  attachmentCount: number
): boolean {
  if (!photosRequired) return true;
  return attachmentCount >= 1;
}
